/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { AttachIcon, SendIcon } from 'components/elements/Icons';
import { TextField, Attachment } from 'components/elements';
import { useDeviceDetect, useAttachmentList } from 'hooks';

import Styles from './styles';

const useStyles = makeStyles(Styles);

const MAX_UPLOADS_SIZE = 20 * 1000 * 125 * 8;

function ChatMessageSendForm({ onReply, classes, allowAttachments, attachmentUploadEnabled }) {
  const styles = useStyles();
  const { isDesktop } = useDeviceDetect();
  const { attachments, attachFile, deleteFile, clearAttachments } = useAttachmentList(MAX_UPLOADS_SIZE);
  const { t } = useTranslation('labels');
  const { handleSubmit, control, watch, reset } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      message: '',
    },
  });
  const message = watch('message');

  const sendMessage = async (data) => {
    if (data?.message.length || attachments?.length) {
      reset();
      await onReply({ ...data, attachments });
      clearAttachments();
    }
  };

  const attachFileEvent = (e) => attachFile(e.target.files);

  const sendBtnClasses = cn(styles.sendBtn, { [styles.sendBtnActive]: message?.length || attachments?.length });
  const messageInputClasses = cn(styles.messageInput, { [styles.messageInputActive]: attachments?.length });
  const controlsClasses = cn(styles.controls, classes, { [styles.extendedControls]: attachments?.length });
  const attachIconClasses = cn(styles.attachIcon, { [styles.attachIconActive]: attachmentUploadEnabled });

  return (
    <form className={controlsClasses} onSubmit={handleSubmit(sendMessage)}>
      {allowAttachments && (
        <button className={styles.attachFile} type="button">
          <label htmlFor="attach">
            <AttachIcon classes={attachIconClasses} />
            <span className="sr-only">{t('attachments')}</span>
          </label>
          <input
            type="file"
            name="attach"
            id="attach"
            accept="image/gif, image/jpeg, image/png, application/pdf"
            onChange={attachFileEvent}
            disabled={!attachmentUploadEnabled}
            onClick={(e) => {
              e.target.value = '';
            }}
          />
        </button>
      )}

      <div className={messageInputClasses}>
        {attachments.length > 0 && (
          <div className={styles.attachmentsBox}>
            <div className={styles.scrollWrap}>
              {attachments?.map(({ name }, i) => (
                <Attachment classes={styles.attachment} onDeleteFile={deleteFile(i)} key={name} text={name} isExpired={false} isNew />
              ))}
            </div>
          </div>
        )}
        <Controller
          name="message"
          control={control}
          render={({ field: { ref, ...restField } }) => (
            <>
              <label htmlFor="enter-message-input" className="sr-only">
                {t('enter_message_here')}
              </label>
              <TextField
                size={isDesktop ? 'medium' : 'small'}
                style={{ marginTop: 0 }}
                placeholder={t('enter_message_here')}
                id="enter-message-input"
                {...restField}
                autoComplete="off"
                multiline
                onKeyPress={(e) => {
                  if (e.code === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const data = { message };
                    sendMessage(data);
                  }
                }}
              />
            </>
          )}
        />
      </div>
      <button className={sendBtnClasses} type="submit">
        <SendIcon />
        <span className="sr-only">{t('send')}</span>
      </button>
    </form>
  );
}

ChatMessageSendForm.propTypes = {
  onReply: PropTypes.func.isRequired,
  allowAttachments: PropTypes.bool,
  attachmentUploadEnabled: PropTypes.bool,
  classes: PropTypes.string,
};

ChatMessageSendForm.defaultProps = {
  classes: '',
  allowAttachments: false,
  attachmentUploadEnabled: true,
};

export default ChatMessageSendForm;
